/**
 * App config
 *
 * Reads from three sources (in priority order):
 * 1. window.RUNTIME_CONFIG - injected at container startup (Docker)
 * 2. process.env - from build-time environment (.env file)
 * 3. Hard-coded defaults - when neither runtime nor build-time available
 */

const getConfigValue = (key, buildTimeValue, hardcodedDefault) => {
    // Priority 1: Runtime config (Docker container startup)
    if (typeof window !== 'undefined' && window.RUNTIME_CONFIG
        && window.RUNTIME_CONFIG[key] !== undefined && window.RUNTIME_CONFIG[key] !== '') {
        return window.RUNTIME_CONFIG[key];
    }
    // Priority 2: Build-time value (from .env during npm run build)
    if (buildTimeValue !== undefined && buildTimeValue !== '') {
        return buildTimeValue;
    }
    // Priority 3: Hard-coded default (fallback safety net)
    return hardcodedDefault;
};

export default {
    // General
    // APP_VERSION and APP_BUILD are build-time only (not overridable by Docker env vars)
    APP_VERSION: process.env.APP_VERSION || '',
    APP_BUILD: process.env.APP_BUILD || '',
    DEPLOYMENT_ENV: getConfigValue('DEPLOYMENT_ENV', process.env.DEPLOYMENT_ENV, undefined),
    PUBLIC_URL: getConfigValue('PUBLIC_URL', process.env.PUBLIC_URL, '/'),
    BACKDOOR_URL: getConfigValue('BACKDOOR_URL', process.env.BACKDOOR_URL, undefined),
    LOCALE: getConfigValue('LOCALE', process.env.LOCALE, 'en'),
    COOKIE_DOMAIN: getConfigValue('COOKIE_DOMAIN', process.env.COOKIE_DOMAIN, undefined),
    DATADOG_RUM_APPLICATION_ID: getConfigValue('DATADOG_RUM_APPLICATION_ID', process.env.DATADOG_RUM_APPLICATION_ID, undefined),
    DATADOG_CLIENT_TOKEN: getConfigValue('DATADOG_CLIENT_TOKEN', process.env.DATADOG_CLIENT_TOKEN, undefined),
    DATADOG_SERVICE: getConfigValue('DATADOG_SERVICE', process.env.DATADOG_SERVICE, undefined),
    DATADOG_ENABLE: (getConfigValue('DATADOG_ENABLE', process.env.DATADOG_ENABLE, 'false')) === 'true',
    ACE_LOCALE_TRANSLATION_EN_US_URL: getConfigValue('ACE_LOCALE_TRANSLATION_EN_US_URL', process.env.ACE_LOCALE_TRANSLATION_EN_US_URL, undefined),
    ACE_LOCALE_TRANSLATION_DE_DE_URL: getConfigValue('ACE_LOCALE_TRANSLATION_DE_DE_URL', process.env.ACE_LOCALE_TRANSLATION_DE_DE_URL, undefined),
    ACE_KEYCLOAK_SERVICE_URL: getConfigValue('ACE_KEYCLOAK_SERVICE_URL', process.env.ACE_KEYCLOAK_SERVICE_URL, undefined),
    ACE_KEYCLOAK_REALM: getConfigValue('ACE_KEYCLOAK_REALM', process.env.ACE_KEYCLOAK_REALM, undefined),
    ACE_KEYCLOAK_CLIENT_ID: getConfigValue('ACE_KEYCLOAK_CLIENT_ID', process.env.ACE_KEYCLOAK_CLIENT_ID, undefined),
    ACE_USER_PROFILE_EUP_SERVICE_URL: getConfigValue('ACE_USER_PROFILE_EUP_SERVICE_URL', process.env.ACE_USER_PROFILE_EUP_SERVICE_URL, ''),
    ACE_ECS_FLOW_EF_SERVICE_URL: getConfigValue('ACE_ECS_FLOW_EF_SERVICE_URL', process.env.ACE_ECS_FLOW_EF_SERVICE_URL, ''),
    ACE_PARTNER_MANAGEMENT_APM_SERVICE_URL: getConfigValue('ACE_PARTNER_MANAGEMENT_APM_SERVICE_URL', process.env.ACE_PARTNER_MANAGEMENT_APM_SERVICE_URL, ''),
    ACE_LEA_ASSIGNMENT_FLOW_ALF_SERVICE_URL: getConfigValue('ACE_LEA_ASSIGNMENT_FLOW_ALF_SERVICE_URL', process.env.ACE_LEA_ASSIGNMENT_FLOW_ALF_SERVICE_URL, ''),
    ACE_PRICING_MANAGEMENT_APS_SERVICE_URL: getConfigValue('ACE_PRICING_MANAGEMENT_APS_SERVICE_URL', process.env.ACE_PRICING_MANAGEMENT_APS_SERVICE_URL, ''),
    // Hard-coded constants (not injectable)
    DEFAULT_PAGE_SIZE: 20,
    INITIAL_PAGE_INDEX: 0,
    ACTIVE_SERVICE_CASES_RESULTS_COUNT: 3,
    ACE_ECS_SITE_URL: getConfigValue('ACE_ECS_SITE_URL', process.env.ACE_ECS_SITE_URL, undefined),
    ECS_SERVICE_CASE_SCREEN_PATH: '/service-cases/:serviceCaseId',
    ACE_AC_SYSTEM_LABEL_TRANSLATION: getConfigValue('ACE_AC_SYSTEM_LABEL_TRANSLATION', process.env.ACE_AC_SYSTEM_LABEL_TRANSLATION, undefined),
    ACE_VPM_SYSTEM_LABEL_TRANSLATION: getConfigValue('ACE_VPM_SYSTEM_LABEL_TRANSLATION', process.env.ACE_VPM_SYSTEM_LABEL_TRANSLATION, undefined),
    EUA_ECS_SYSTEM_LABEL_TRANSLATION: getConfigValue('EUA_ECS_SYSTEM_LABEL_TRANSLATION', process.env.EUA_ECS_SYSTEM_LABEL_TRANSLATION, undefined),
    EUA_LEA_SYSTEM_LABEL_TRANSLATION: getConfigValue('EUA_LEA_SYSTEM_LABEL_TRANSLATION', process.env.EUA_LEA_SYSTEM_LABEL_TRANSLATION, undefined),
    MAX_STANDBY_ENTRIES_ALLOWED: 60,
    MAXIMUM_INPUT_CONTENT_LENGTH: 50,
    MIN_ULTRA_HEAVY_WEIGHT_VEHICLE_TONNAGE: 7.51,
    CURRENCY: getConfigValue('CURRENCY', process.env.CURRENCY, 'EUR'),
    CONTRACT_PARTNERS_SEARCH_COUNTRY_CODE: 'DE',
    ARCGIS_OAUTH2_SERVICE_URL: getConfigValue('ARCGIS_OAUTH2_SERVICE_URL', process.env.ARCGIS_OAUTH2_SERVICE_URL, undefined),
    ARCGIS_OAUTH2_CLIENT_ID: getConfigValue('ARCGIS_OAUTH2_CLIENT_ID', process.env.ARCGIS_OAUTH2_CLIENT_ID, undefined),
    ARCGIS_SERVICE_URL: getConfigValue('ARCGIS_SERVICE_URL', process.env.ARCGIS_SERVICE_URL, undefined),
    TEMP_ARCGIS_MAP_ID: getConfigValue('TEMP_ARCGIS_MAP_ID', process.env.TEMP_ARCGIS_MAP_ID, ''),
    ARCGIS_CONTRACT_PARTNER_LOCATIONS_LAYER_URL: getConfigValue('ARCGIS_CONTRACT_PARTNER_LOCATIONS_LAYER_URL', process.env.ARCGIS_CONTRACT_PARTNER_LOCATIONS_LAYER_URL, ''),
    ARCGIS_CONTRACT_PARTNER_SERVICE_AREAS_LAYER_URL: getConfigValue('ARCGIS_CONTRACT_PARTNER_SERVICE_AREAS_LAYER_URL', process.env.ARCGIS_CONTRACT_PARTNER_SERVICE_AREAS_LAYER_URL, ''),
    TEMPORARY_RESTRICTIONS_DEFAULT_PAGE_SIZE: 10,
    DEFAULT_TIME_PERIOD_IN_MONTHS: 6,
    ACE_APM_QUALITY_MANAGEMENT_FEEDBACKS_EXPORT_ENDPOINT: getConfigValue('ACE_APM_QUALITY_MANAGEMENT_FEEDBACKS_EXPORT_ENDPOINT', process.env.ACE_APM_QUALITY_MANAGEMENT_FEEDBACKS_EXPORT_ENDPOINT, ''),
    ACE_APM_FILE_ASSETS_SERVICE_ENDPOINT: getConfigValue('ACE_APM_FILE_ASSETS_SERVICE_ENDPOINT', process.env.ACE_APM_FILE_ASSETS_SERVICE_ENDPOINT, ''),
    ACE_EUP_FILE_ASSETS_SERVICE_ENDPOINT: getConfigValue('ACE_EUP_FILE_ASSETS_SERVICE_ENDPOINT', process.env.ACE_EUP_FILE_ASSETS_SERVICE_ENDPOINT, ''),
    MAXIMUM_QMN_DESCRIPTION_TEXT_AREA_CONTENT_LENGTH: 1000,
    MAXIMUM_FILE_ASSETS_FILE_SIZE_BYTES: 52428800,
    MAXIMUM_FILE_ASSETS_IN_VIEW: 5,
    MINIMUM_SEARCH_QUERY_LENGTH: 3,
    MAXIMUM_TEXT_AREA_CONTENT_LENGTH: 250,
    MAXIMUM_PHONE_EXTENSION_DIGITS: 4,
    MAXIMUM_INA_RULE_DUNNING_LEVEL: 6,
    MAXIMUM_INTEGER_SIZE: 2147483647,
    BUDGETS_DATE_INTERSECTION: '01.01.2025',
};
